import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ArrowLeft, FileText, CheckCircle, XCircle, Clock, Zap } from "lucide-react";
import { API } from "@/config/api";

const AdminRequests_v2 = () => {
  const navigate = useNavigate();
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showApproveDialog, setShowApproveDialog] = useState(false);
  const [showDeclineDialog, setShowDeclineDialog] = useState(false);
  const [approveData, setApproveData] = useState({ amount_approved: "", repayment_plan_text: "" });
  const [declineReason, setDeclineReason] = useState("");
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    loadRequests();
  }, []);

  const loadRequests = async () => {
    try {
      const response = await axios.get(`${API}/admin/help-requests`, {
        withCredentials: true
      });
      setRequests(response.data);
    } catch (error) {
      toast.error('Failed to load requests');
    } finally {
      setLoading(false);
    }
  };

  const handleApprove = async () => {
    if (!approveData.amount_approved || !approveData.repayment_plan_text) {
      toast.error('Please fill all fields');
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(
        `${API}/admin/help-request/${selectedRequest.request_id}/approve`,
        { 
          amount_approved: parseFloat(approveData.amount_approved),
          repayment_plan_text: approveData.repayment_plan_text
        },
        { withCredentials: true }
      );
      
      toast.success('Request approved!');
      setShowApproveDialog(false);
      setApproveData({ amount_approved: "", repayment_plan_text: "" });
      loadRequests();
    } catch (error) {
      toast.error('Failed to approve request');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDecline = async () => {
    if (!declineReason) {
      toast.error('Please provide a reason');
      return;
    }

    setActionLoading(true);
    try {
      await axios.post(
        `${API}/admin/help-request/${selectedRequest.request_id}/decline`,
        { reason: declineReason },
        { withCredentials: true }
      );
      
      toast.success('Request declined');
      setShowDeclineDialog(false);
      setDeclineReason("");
      loadRequests();
    } catch (error) {
      toast.error('Failed to decline request');
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-yellow-400"></div>
      </div>
    );
  }

  const statusIcons = {
    pending: <Clock className="h-5 w-5 text-yellow-400" />,
    approved: <CheckCircle className="h-5 w-5 text-green-400" />,
    declined: <XCircle className="h-5 w-5 text-red-400" />
  };

  const statusColors = {
    pending: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
    approved: "bg-green-500/20 text-green-400 border-green-500/30",
    declined: "bg-red-500/20 text-red-400 border-red-500/30"
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 py-12 px-6">
      {/* Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-black/80 backdrop-blur-xl border-b border-white/10">
        <div className="container mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="h-8 w-8 text-yellow-400" />
            <span className="text-2xl font-heading font-bold text-white">DueDay</span>
          </div>
          <span className="px-4 py-2 bg-yellow-400/20 border border-yellow-400/50 rounded-full text-yellow-400 text-sm font-semibold">
            Admin Panel
          </span>
        </div>
      </nav>

      <div className="container mx-auto max-w-7xl pt-16">
        <Button 
          data-testid="back-to-admin-btn"
          variant="ghost" 
          onClick={() => navigate('/admin')}
          className="mb-6 rounded-full text-white hover:text-yellow-400 hover:bg-white/10"
        >
          <ArrowLeft className="mr-2 h-4 w-4" /> Back to Admin
        </Button>

        <div className="bg-slate-800/50 border border-slate-700 rounded-2xl p-8 md:p-12">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h1 className="text-4xl font-heading font-bold mb-2 text-white" data-testid="requests-title">
                Help Requests
              </h1>
              <p className="text-slate-400">{requests.length} total requests</p>
            </div>
          </div>

          {requests.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 text-slate-600 mx-auto mb-4" />
              <p className="text-slate-400">No requests yet</p>
            </div>
          ) : (
            <div className="space-y-4">
              {requests.map((request) => (
                <div 
                  key={request.request_id}
                  data-testid={`request-${request.request_id}`}
                  className="p-6 border border-slate-700 rounded-2xl hover:border-slate-600 transition-all bg-slate-900/50"
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex items-start gap-4 flex-1">
                      {statusIcons[request.status]}
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-xl font-heading font-bold capitalize text-white">
                            {request.bill_type.replace('-', ' ')}
                          </h3>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium border ${statusColors[request.status]}`}>
                            {request.status}
                          </span>
                        </div>
                        <p className="text-sm text-slate-400 mb-2">
                          {request.user?.name} ({request.user?.email}) - {request.user?.city}, {request.user?.state}
                        </p>
                        <p className="text-sm mb-2 text-slate-300">{request.description}</p>
                        <div className="flex gap-6 text-sm text-slate-500">
                          <span>Due: {new Date(request.due_date).toLocaleDateString()}</span>
                          <span>Submitted: {new Date(request.created_at).toLocaleDateString()}</span>
                          <span>Tier: <span className="capitalize text-slate-400">{request.user?.membership_tier || 'none'}</span></span>
                        </div>
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      <div className="text-3xl font-heading font-bold text-yellow-400 mb-2">
                        ${request.amount_requested}
                      </div>
                      {request.status === 'pending' && (
                        <div className="flex gap-2">
                          <Button
                            data-testid={`approve-${request.request_id}-btn`}
                            size="sm"
                            onClick={() => {
                              setSelectedRequest(request);
                              setApproveData({ 
                                amount_approved: request.amount_requested.toString(), 
                                repayment_plan_text: "" 
                              });
                              setShowApproveDialog(true);
                            }}
                            className="bg-green-600 hover:bg-green-700 text-white rounded-full"
                          >
                            Approve
                          </Button>
                          <Button
                            data-testid={`decline-${request.request_id}-btn`}
                            size="sm"
                            onClick={() => {
                              setSelectedRequest(request);
                              setShowDeclineDialog(true);
                            }}
                            className="bg-red-600 hover:bg-red-700 text-white rounded-full"
                          >
                            Decline
                          </Button>
                        </div>
                      )}
                      {request.status === 'declined' && request.decline_reason && (
                        <p className="text-sm text-red-400 mt-2">
                          Reason: {request.decline_reason}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Approve Dialog */}
      <Dialog open={showApproveDialog} onOpenChange={setShowApproveDialog}>
        <DialogContent className="bg-slate-800 border-slate-700" data-testid="approve-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Approve Request</DialogTitle>
            <DialogDescription className="text-slate-400">
              Enter the approved amount and repayment plan for this request.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div>
              <Label htmlFor="amount" className="text-white" data-testid="approve-amount-label">Amount to Approve</Label>
              <Input
                id="amount"
                data-testid="approve-amount-input"
                type="number"
                step="0.01"
                placeholder="150.00"
                value={approveData.amount_approved}
                onChange={(e) => setApproveData({...approveData, amount_approved: e.target.value})}
                className="mt-2 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-yellow-400"
              />
            </div>
            <div>
              <Label htmlFor="repayment" className="text-white" data-testid="repayment-plan-label">Repayment Plan</Label>
              <Textarea
                id="repayment"
                data-testid="repayment-plan-textarea"
                placeholder="e.g., 5 weekly payments of $30"
                value={approveData.repayment_plan_text}
                onChange={(e) => setApproveData({...approveData, repayment_plan_text: e.target.value})}
                className="mt-2 bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-yellow-400"
              />
            </div>
            <div className="flex gap-3">
              <Button
                data-testid="confirm-approve-btn"
                onClick={handleApprove}
                disabled={actionLoading}
                className="bg-yellow-400 text-black hover:bg-yellow-300 rounded-full flex-1 font-bold"
              >
                {actionLoading ? 'Approving...' : 'Confirm Approval'}
              </Button>
              <Button
                data-testid="cancel-approve-btn"
                onClick={() => setShowApproveDialog(false)}
                variant="outline"
                className="border-slate-600 text-white hover:bg-slate-700 rounded-full flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Decline Dialog */}
      <Dialog open={showDeclineDialog} onOpenChange={setShowDeclineDialog}>
        <DialogContent className="bg-slate-800 border-slate-700" data-testid="decline-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Decline Request</DialogTitle>
            <DialogDescription className="text-slate-400">
              Please provide a reason for declining this request.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <Textarea
              data-testid="decline-reason-textarea"
              placeholder="e.g., Amount exceeds tier limit"
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              className="bg-slate-900/50 border-slate-600 text-white placeholder:text-slate-500 focus:border-yellow-400"
            />
            <div className="flex gap-3">
              <Button
                data-testid="confirm-decline-btn"
                onClick={handleDecline}
                disabled={actionLoading}
                className="bg-red-600 hover:bg-red-700 text-white rounded-full flex-1"
              >
                {actionLoading ? 'Declining...' : 'Confirm Decline'}
              </Button>
              <Button
                data-testid="cancel-decline-btn"
                onClick={() => setShowDeclineDialog(false)}
                variant="outline"
                className="border-slate-600 text-white hover:bg-slate-700 rounded-full flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminRequests_v2;
